import React, { useState, useEffect, useMemo, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import { Link } from 'react-router-dom';
import L from 'leaflet';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { useLocationScope } from '../context/LocationContext';
import LoadingSpinner from '../components/LoadingSpinner';
import MetroSwitcher from '../components/MetroSwitcher';
import FeatureFilterChips, { FilterChipList } from '../components/FeatureFilterChips';
import { filterBarsByFeatures, filterBarsByAreas, getAreaFilterOptions } from '../utils/barFilters';
import NearbyCheckInButton from '../components/NearbyCheckInButton';
import { MapPin, Star, Info, X, User, LogIn, LocateFixed, Loader2, PlusCircle } from 'lucide-react';
import { getBarLocationLabel } from '../utils/barLocation';
import { getFeaturesForMetro } from '../data/features';

// User location marker (blue dot)
const createUserLocationIcon = () => {
  return L.divIcon({
    className: 'user-location-marker',
    html: `
      <div style="
        width: 20px;
        height: 20px;
        background: #3B82F6;
        border: 3px solid #fff;
        border-radius: 50%;
        box-shadow: 0 0 15px rgba(59, 130, 246, 0.6), 0 2px 6px rgba(0,0,0,0.3);
      "></div>
    `,
    iconSize: [20, 20],
    iconAnchor: [10, 10]
  });
};

// Component to handle map recentering
function RecenterMap({ position, zoom = 12 }) {
  const map = useMap();
  
  useEffect(() => {
    if (position) {
      map.flyTo(position, zoom, { duration: 1 });
    }
  }, [map, position, zoom]);
  
  return null;
}

// Custom marker icon with multiple states
export const createMarkerIcon = (isDive, isRated, isBoth) => {
  // Colors
  const diveColor = '#ff2d55';      // Red/pink for dive bars
  const ratedColor = '#F59E0B';     // Amber for rated bars
  const defaultColor = '#3a3a42';   // Gray for unrated/non-dive
  
  let bgColor = defaultColor;
  let borderColor = '#666';
  let glowColor = '#000';
  let glowSize = '5px';
  let strokeColor = '#999';

  if (isBoth) {
    // Both dive bar AND rated - amber with red accent
    bgColor = ratedColor;
    borderColor = diveColor;
    glowColor = ratedColor;
    glowSize = '15px';
    strokeColor = '#fff';
  } else if (isDive) {
    bgColor = diveColor;
    borderColor = '#ffb700';
    glowColor = diveColor;
    glowSize = '15px';
    strokeColor = '#fff';
  } else if (isRated) {
    bgColor = ratedColor;
    borderColor = '#D97706';
    glowColor = ratedColor;
    glowSize = '12px';
    strokeColor = '#fff';
  }

  return L.divIcon({
    className: 'custom-marker',
    html: `
      <div style="
        width: 32px;
        height: 32px;
        background: ${bgColor};
        border: 3px solid ${borderColor};
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 0 ${glowSize} ${glowColor};
        cursor: pointer;
        transition: all 0.2s;
      ">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="${strokeColor}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M17 11h1a3 3 0 0 1 0 6h-1"/>
          <path d="M9 12v6"/>
          <path d="M13 12v6"/>
          <path d="M14 7.5c-1 0-1.44.5-3 .5s-2-.5-3-.5-1.72.5-2.5.5a2.5 2.5 0 0 1 0-5c.78 0 1.57.5 2.5.5S9.44 2.5 11 2.5s2 .5 3 .5 1.72-.5 2.5-.5a2.5 2.5 0 0 1 0 5c-.78 0-1.5-.5-2.5-.5Z"/>
          <path d="M5 8v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V8"/>
        </svg>
      </div>
    `,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32]
  });
};

function MapView() {
  const { bars, ratings, getBarStats, loading } = useApp();
  const { user } = useAuth();
  const {
    activeMetro,
    mapCenter,
    getScopedBars,
    userLocationArray,
    locationAccuracy,
    locationLoading,
    locationError,
    enableGpsScope,
    showAreaFilter,
    areaLabel,
    areas,
    usingGps,
  } = useLocationScope();
  const scopedBars = useMemo(() => getScopedBars(bars), [bars, getScopedBars]);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [showFeaturePanel, setShowFeaturePanel] = useState(false);
  const [showAreaPanel, setShowAreaPanel] = useState(false);
  
  // Filter states - load from localStorage
  const [diveOnly, setDiveOnly] = useState(() => {
    const saved = localStorage.getItem('tilted-filter-dive');
    return saved === 'true';
  });
  const [myRatedOnly, setMyRatedOnly] = useState(() => {
    const saved = localStorage.getItem('tilted-filter-rated');
    return saved === 'true';
  });
  const [selectedNeighborhoods, setSelectedNeighborhoods] = useState([]);
  const [showDiveBarInfo, setShowDiveBarInfo] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  
  const userLocation = userLocationArray;
  const [recenterTrigger, setRecenterTrigger] = useState(null);
  
  // Reset scroll position when MapView mounts (fixes issue where filter buttons hide when returning from bar detail)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const lastMetroRef = useRef(activeMetro?.id);
  useEffect(() => {
    if (lastMetroRef.current === activeMetro?.id) return;
    lastMetroRef.current = activeMetro?.id;
    setSelectedNeighborhoods([]);
    setSelectedFeatures([]);
    setDiveOnly(false);
    setMyRatedOnly(false);
    setShowFeaturePanel(false);
    setShowAreaPanel(false);
  }, [activeMetro?.id]);

  useEffect(() => {
    if (usingGps && userLocation) {
      setRecenterTrigger([...userLocation]);
    }
  }, [userLocation, usingGps]);

  useEffect(() => {
    if (!usingGps && mapCenter) {
      setRecenterTrigger([mapCenter[0], mapCenter[1]]);
    }
  }, [activeMetro?.id, usingGps, mapCenter[0], mapCenter[1]]);
  
  const handleRecenter = () => {
    if (userLocation) {
      setRecenterTrigger([...userLocation]);
    } else {
      localStorage.removeItem('tilted_location_asked');
      enableGpsScope();
    }
  };

  // Save filter states to localStorage
  useEffect(() => {
    localStorage.setItem('tilted-filter-dive', diveOnly.toString());
  }, [diveOnly]);

  useEffect(() => {
    localStorage.setItem('tilted-filter-rated', myRatedOnly.toString());
  }, [myRatedOnly]);

  // Get user's rated bar IDs
  const userRatedBarIds = useMemo(() => {
    if (!user) return new Set();
    return new Set(ratings.filter(r => r.userId === user.id).map(r => r.barId));
  }, [user, ratings]);

  // Check if user has rated a bar
  const hasUserRated = (barId) => userRatedBarIds.has(barId);

  const filteredBars = useMemo(() => {
    let result = [...scopedBars];

    if (diveOnly) {
      result = result.filter(bar => {
        const stats = getBarStats(bar.id);
        return stats.divePercentage > 50;
      });
    }

    if (myRatedOnly && user) {
      result = result.filter(bar => hasUserRated(bar.id));
    }

    if (selectedNeighborhoods.length > 0) {
      result = filterBarsByAreas(result, selectedNeighborhoods, activeMetro);
    }

    if (selectedFeatures.length > 0) {
      result = filterBarsByFeatures(result, selectedFeatures);
    }

    return result;
  }, [scopedBars, diveOnly, myRatedOnly, user, selectedNeighborhoods, selectedFeatures, getBarStats, hasUserRated]);

  const neighborhoods = useMemo(
    () => getAreaFilterOptions(areas, scopedBars, activeMetro),
    [areas, scopedBars, activeMetro]
  );

  useEffect(() => {
    const allowed = new Set(getFeaturesForMetro(activeMetro?.id).map((feature) => feature.key));
    setSelectedFeatures((prev) => prev.filter((key) => allowed.has(key)));
  }, [activeMetro?.id]);

  const handleMyRatedToggle = () => {
    if (!user) {
      setShowLoginPrompt(true);
      setTimeout(() => setShowLoginPrompt(false), 3000);
      return;
    }
    setMyRatedOnly(!myRatedOnly);
  };

  const metroName = activeMetro?.name || 'this city';
  const metroCount = scopedBars.length;

  const getFilterDescription = () => {
    if (diveOnly && myRatedOnly) return 'Your rated dive bars';
    if (diveOnly) return 'Community rated dive bars';
    if (myRatedOnly) return 'Bars you\'ve rated';
    return metroName;
  };
  
  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="relative overflow-hidden" style={{ height: 'calc(100vh - var(--app-top-offset))' }}>
      {/* Map Controls - fixed positioning to prevent scroll issues when returning from bar detail */}
      {/* On mobile: top-16 (4rem) = below h-14 nav + small gap. On desktop: top-4 since no top nav */}
      <div className="fixed top-16 left-2 right-2 md:top-4 md:left-24 md:right-4 z-[1000] pointer-events-none" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        {/* First row - Logo, filters, legend. The float reserves room beside the top-right Check-in/Add Bar
            buttons so only the line next to them gets shorter.
            Phone: Dive Bars Only | My Rated / Location | Map Legend. iPad+: Dive Bars Only | Location | My Rated Bars | Map Legend. */}
        <div className="flow-root">
          <div aria-hidden="true" className="float-right h-12 w-24 sm:w-[10.5rem]" />
          <div className="glass rounded-lg px-2 py-1 h-10 hidden md:inline-flex align-top mr-2 mb-2 items-center pointer-events-auto">
            <img src="/logo-nameplate.png" alt="Tilted" className="h-full w-auto object-contain bg-transparent" />
          </div>
          
          <button
            onClick={() => setDiveOnly(!diveOnly)}
            className={`glass rounded-lg px-3 py-2 h-10 text-sm transition-all inline-flex align-top mr-2 mb-2 items-center gap-2 pointer-events-auto ${
              diveOnly 
                ? 'text-dive-neon border border-dive-neon bg-dive-neon/10' 
                : 'text-gray-400 hover:text-white'
            }`}
            title="Show only Community Rated Dive Bars (>50% voted dive)"
          >
            Dive Bars Only
          </button>

          <button
            onClick={handleMyRatedToggle}
            className={`glass rounded-lg px-3 py-2 h-10 text-sm transition-all md:hidden inline-flex align-top mr-2 mb-2 items-center gap-2 pointer-events-auto ${
              myRatedOnly && user
                ? 'text-amber-400 border border-amber-500 bg-amber-500/10' 
                : user 
                  ? 'text-gray-400 hover:text-amber-400'
                  : 'text-gray-600 cursor-not-allowed'
            }`}
            title={user ? "Show only bars you've rated" : "Log in to see your rated bars"}
          >
            My Rated<span className="hidden sm:inline">&nbsp;Bars</span>
          </button>
          {/* Phone: start a new line after My Rated */}
          <div aria-hidden="true" className="block md:hidden" />

          <div className="glass rounded-lg px-2 py-1 h-10 inline-flex align-top mr-2 mb-2 items-center pointer-events-auto">
            <MetroSwitcher compact />
          </div>

          <button
            onClick={handleMyRatedToggle}
            className={`glass rounded-lg px-3 py-2 h-10 text-sm transition-all hidden md:inline-flex align-top mr-2 mb-2 items-center gap-2 pointer-events-auto ${
              myRatedOnly && user
                ? 'text-amber-400 border border-amber-500 bg-amber-500/10' 
                : user 
                  ? 'text-gray-400 hover:text-amber-400'
                  : 'text-gray-600 cursor-not-allowed'
            }`}
            title={user ? "Show only bars you've rated" : "Log in to see your rated bars"}
          >
            My Rated Bars
          </button>

          <button
            onClick={() => setShowDiveBarInfo(!showDiveBarInfo)}
            className="glass rounded-lg px-3 py-2 h-10 text-sm text-gray-300 hover:text-white inline-flex align-top mr-2 mb-2 items-center gap-2 pointer-events-auto"
          >
            <Info className="w-4 h-4" />
            Map Legend
          </button>
        </div>
        
        {/* Second row - Neighborhoods, features */}
        <div className="flex flex-wrap gap-2 items-center w-fit max-w-full pointer-events-auto">
          {showAreaFilter && (
            <button
              onClick={() => {
                setShowAreaPanel((prev) => !prev);
                setShowFeaturePanel(false);
              }}
              className={`glass rounded-lg px-3 py-2 text-sm ${
                selectedNeighborhoods.length || showAreaPanel ? 'text-dive-amber border border-amber-500' : 'text-gray-400'
              }`}
            >
              {areaLabel === 'Neighborhood' ? 'Neighborhoods' : `${areaLabel}s`}
              {selectedNeighborhoods.length ? ` (${selectedNeighborhoods.length})` : ''}
            </button>
          )}

          <button
            onClick={() => {
              setShowFeaturePanel((prev) => !prev);
              setShowAreaPanel(false);
            }}
            className={`glass rounded-lg px-3 py-2 text-sm ${
              selectedFeatures.length || showFeaturePanel ? 'text-dive-amber border border-amber-500' : 'text-gray-400'
            }`}
          >
            Features{selectedFeatures.length ? ` (${selectedFeatures.length})` : ''}
          </button>

        </div>
        {showAreaPanel && showAreaFilter && (
          <div className="mt-2 glass rounded-lg p-3 max-w-xs pointer-events-auto">
            <FilterChipList
              items={neighborhoods}
              selected={selectedNeighborhoods}
              onToggle={(key) => setSelectedNeighborhoods((prev) =>
                prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
              )}
            />
          </div>
        )}
        {showFeaturePanel && (
          <div className="mt-2 glass rounded-lg p-3 max-w-xs pointer-events-auto">
            <FeatureFilterChips
              metroId={activeMetro?.id}
              selectedFeatures={selectedFeatures}
              onToggle={(key) => setSelectedFeatures((prev) =>
                prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key]
              )}
            />
          </div>
        )}
        {showLoginPrompt && (
          <div className="mt-2 glass rounded-lg p-3 flex items-center gap-2 text-amber-400 animate-pulse w-fit pointer-events-auto">
            <LogIn className="w-4 h-4" />
            <span className="text-sm">Log in to see your rated bars</span>
          </div>
        )}
        {showDiveBarInfo && (
          <div className="mt-2 glass rounded-lg p-4 max-w-sm pointer-events-auto">
            <div className="flex items-start justify-between mb-2">
              <h4 className="text-white font-medium">Map Legend</h4>
              <button
                onClick={() => setShowDiveBarInfo(false)}
                className="text-gray-400 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-[#ff2d55] border-2 border-[#ffb700]"></div>
                <div>
                  <span className="text-white">Community Rated Dive Bar</span>
                  <p className="text-gray-400 text-xs">
                    &gt;50% of users voted this as a dive bar
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-[#F59E0B] border-2 border-[#D97706]"></div>
                <div>
                  <span className="text-white">Your Rated Bar</span>
                  <p className="text-gray-400 text-xs">
                    You've rated this bar
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-[#F59E0B] border-2 border-[#ff2d55]"></div>
                <div>
                  <span className="text-white">Rated Dive Bar</span>
                  <p className="text-gray-400 text-xs">
                    You've rated + Community Rated Dive
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                <div className="w-6 h-6 rounded-full bg-dive-gray border-2 border-gray-500"></div>
                <div>
                  <span className="text-white">Other Bars</span>
                  <p className="text-gray-400 text-xs">
                    Not yet rated by you or community
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Check-in + Add Bar - Top Right */}
      <div className="fixed top-16 right-2 md:top-4 md:right-4 z-[1000] flex items-center gap-2" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}>
        {user && <NearbyCheckInButton variant="circle" />}

        {/* Add Bar Button */}
        <Link
          to="/add"
          className="flex items-center gap-2 px-3 py-2 bg-dive-green/20 border border-dive-green text-dive-green rounded-lg hover:bg-dive-green/30 transition-all backdrop-blur-sm"
        >
          <PlusCircle className="w-4 h-4" />
          <span className="text-sm font-medium hidden sm:inline">Add Bar</span>
        </Link>
      </div>

      {/* Stats Banner + Locate Button - above the map attribution, clear of the home indicator */}
      <div
        className="fixed left-4 md:left-24 z-[1000] flex flex-col-reverse items-end gap-3 sm:flex-row sm:items-end pointer-events-none"
        style={{
          right: 'calc(1rem + env(safe-area-inset-right, 0px))',
          bottom: 'calc(60px + env(safe-area-inset-bottom, 0px))',
        }}
      >
        <div className="w-full sm:w-auto sm:flex-1 min-w-0 pointer-events-auto">
          <div className="glass rounded-lg p-3 flex items-center justify-between max-w-md">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                myRatedOnly ? 'bg-amber-500/20' : 'bg-dive-neon/20'
              }`}>
                <MapPin className={`w-5 h-5 ${myRatedOnly ? 'text-amber-400' : 'text-dive-neon'}`} />
              </div>
              <div>
                <p className="text-white font-medium">
                  {filteredBars.length === 0
                    ? 'No bars found'
                    : filteredBars.length === metroCount
                      ? `${metroCount} ${metroCount === 1 ? 'bar' : 'bars'} in ${metroName}`
                      : `${filteredBars.length} of ${metroCount} in ${metroName}`}
                </p>
                <p className="text-gray-400 text-xs">
                  {metroCount === 0
                    ? `No ${metroName} bars loaded yet`
                    : getFilterDescription()}
                </p>
              </div>
            </div>
            <Link to="/list" className="btn-secondary text-xs px-3 py-1.5 whitespace-nowrap shrink-0">
              View List
            </Link>
          </div>

          {/* Empty state hint */}
          {filteredBars.length === 0 && (
            <div className="glass rounded-lg p-3 mt-2 max-w-md">
              <p className="text-gray-400 text-sm">
                {scopedBars.length === 0
                  ? `No bars in ${activeMetro?.name || 'this city'} yet. Switch cities or add one.`
                  : 'No bars match these filters. Try adjusting your selection.'}
              </p>
            </div>
          )}
        </div>

        <div className="relative shrink-0 sm:ml-auto pointer-events-auto">
          {locationError && locationError.includes('denied') && (
            <div className="absolute bottom-full right-0 mb-2 w-[200px] glass rounded-lg p-2">
              <p className="text-gray-400 text-xs">
                Location disabled. Tap <LocateFixed className="w-3 h-3 inline" /> to retry, or enable in settings.
              </p>
            </div>
          )}
          <button
            onClick={handleRecenter}
            className={`glass rounded-full w-12 h-12 flex items-center justify-center shadow-lg transition-all ${
              userLocation ? 'text-blue-400 hover:bg-blue-500/20' : locationError ? 'text-red-400 hover:text-red-300' : 'text-gray-300 hover:text-white'
            }`}
            title={userLocation ? 'Recenter on my location' : locationError ? 'Enable location' : 'Find my location'}
            aria-label={userLocation ? 'Recenter on my location' : locationError ? 'Enable location' : 'Find my location'}
          >
            {locationLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <LocateFixed className="w-5 h-5" />
            )}
          </button>
        </div>
      </div>

      {/* Map */}
      <MapContainer
        center={mapCenter}
        zoom={12}
        style={{ height: '100%', width: '100%' }}
        zoomControl={false}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        {/* Recenter handler */}
        {recenterTrigger && <RecenterMap position={recenterTrigger} zoom={12} />}
        
        {/* User Location Marker */}
        {userLocation && (
          <>
            {/* Accuracy circle */}
            {locationAccuracy && locationAccuracy < 200 && (
              <Circle
                center={userLocation}
                radius={locationAccuracy}
                pathOptions={{
                  color: '#3B82F6',
                  fillColor: '#3B82F6',
                  fillOpacity: 0.1,
                  weight: 1
                }}
              />
            )}
            {/* User dot */}
            <Marker
              position={userLocation}
              icon={createUserLocationIcon()}
              zIndexOffset={1000}
            >
              <Popup>
                <div className="text-center">
                  <p className="font-medium">You are here</p>
                  {locationAccuracy && (
                    <p className="text-gray-400 text-xs">
                      Accuracy: ±{Math.round(locationAccuracy)}m
                    </p>
                  )}
                </div>
              </Popup>
            </Marker>
          </>
        )}
        
        {filteredBars.map(bar => {
          const stats = getBarStats(bar.id);
          const isDive = stats.divePercentage > 50;
          const isRated = hasUserRated(bar.id);
          const isBoth = isDive && isRated;
          
          return (
            <Marker
              key={bar.id}
              position={[bar.lat, bar.lng]}
              icon={createMarkerIcon(isDive, isRated, isBoth)}
            >
              <Popup>
                <div className="min-w-[210px] text-center">
                  <h3 className="font-bold text-xl mb-0.5">{bar.name}</h3>
                  <p className="text-gray-500 text-xs mb-2">{getBarLocationLabel(bar)}</p>
                  
                  <div className="flex items-center justify-center gap-1.5 mb-3">
                    <Star className="w-5 h-5 fill-dive-amber text-dive-amber" />
                    <span className="font-bold text-xl leading-none">
                      {stats.overall > 0 ? stats.overall.toFixed(1) : 'N/A'}
                    </span>
                    {stats.totalRatings === 0 && (
                      <span className="text-dive-amber text-sm ml-1">Be first to rate!</span>
                    )}
                  </div>

                  <div className="flex flex-wrap justify-center gap-1.5 mb-3">
                    {isDive && (
                      <span className="inline-block bg-red-800 text-white text-xs font-semibold px-2.5 py-1 rounded-full">
                        Dive Bar
                      </span>
                    )}
                    {isRated && (
                      <span className="inline-block bg-amber-500/20 text-amber-400 text-xs px-2.5 py-1 rounded-full">
                        ⭐ Rated
                      </span>
                    )}
                  </div>
                  
                  <Link
                    to={`/bar/${bar.id}`}
                    className="map-popup-btn block w-full text-center py-2 bg-dive-amber hover:bg-amber-600 text-sm font-semibold rounded-lg transition-colors"
                  >
                    View Bar
                  </Link>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
    </div>
  );
}

export default MapView;
